import { useMutation } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import type { AccountsController } from "../../../api/src/accounts/accounts.controller";
import type { AccountWithMetrics } from "../../../api/src/accounts/accounts.service";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type {
  HouseholdGroupForHousehold,
  HouseholdInTwoGroups,
} from "../../../api/src/household-groups/household-groups-on-date";
import type {
  BillingReportAccount,
  FeeStructure,
} from "../../../api/src/billing/lib";
import type { HouseholdsController } from "../../../api/src/households/households.controller";
import type { Household } from "../../../api/src/households-base/households-base.service";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import {
  deserializeAccountWithMetrics,
  useQueryAllAccounts,
} from "../clients/account/lib";
import {
  deserializeHouseholdWithMetrics,
  useQueryHouseholds,
} from "../clients/household/lib";
import ActionButton from "../components/ActionButton";
import { useErrorMessage } from "../components/FormError";
import IndeterminateCheckbox from "../components/IndeterminateCheckbox";
import InfoTooltip from "../components/InfoTooltip";
import ItemAssigningModal from "../components/ItemAssigningModal";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, useTable } from "../components/Table/Table";
import {
  ApiError,
  serverError,
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { parseISODateNaive } from "../lib/date";
import { displayAccountName, naLabel } from "../lib/display";
import { formatCurrency, formatPercent } from "../lib/numbers";
import BillingNav from "./BillingNav";
import { feeLocationDisplay, useQueryFeeStructures } from "./lib";

type BillingHouseholdRow = {
  id: number;
  name: string;
  noOfAccounts: number;
  totalAUM: number;
  feeStructureId: number | null;
  feeStructureName?: string;
  householdGroupId: number | null;
  householdGroupName: string | null;
  estEffectiveFeeRate: number;
  estFee: number;
  estAnnualFee: number;
};

/**
 * The household group of a household row: its one entry of `householdGroups`,
 * or null for a household in no group and for a household in two or more
 * groups (the danger alert's case). The key is optional because a response
 * without it must not crash the page.
 */
const householdGroupOfRow = (
  householdGroups: HouseholdGroupForHousehold[] | undefined,
): HouseholdGroupForHousehold | null => {
  const groups = householdGroups ?? [];
  return groups.length === 1 ? groups[0] : null;
};

/**
 * The group names of a household in two or more household groups, each linked
 * to its group's page inside its quotes. A copy of `quotedNames`
 * (`api/src/household-groups/household-groups-on-date.ts`): `"A"`,
 * `"A" and "B"`, `"A", "B" and "C"`. The client cannot import that file at run
 * time, so the two must be changed together.
 */
const quotedGroupLinks = (
  householdGroups: HouseholdInTwoGroups["householdGroups"],
): ReactNode => {
  const parts = householdGroups.map((group) => (
    <Fragment key={group.householdGroupId}>
      &quot;
      <Link
        to={`/clients/households/household-groups/${group.householdGroupId}`}
      >
        {group.householdGroupName}
      </Link>
      &quot;
    </Fragment>
  ));
  return parts.length <= 1 ? (
    parts
  ) : (
    <>
      {parts.slice(0, -1).map((part, index) => (
        <Fragment key={index}>
          {index === 0 ? null : ", "}
          {part}
        </Fragment>
      ))}
      {" and "}
      {parts[parts.length - 1]}
    </>
  );
};

/**
 * The danger alert for the households that are members of two or more
 * household groups on today's date, or nothing when there are none. The text
 * is a copy of `householdsInTwoGroupsMessage`
 * (`api/src/household-groups/household-groups-on-date.ts`), the sentence of
 * the shared code path's refusal, with each group name linked to its group's
 * page. The date is today's UTC date, the date the api reads the membership
 * on; the summary's `asOfDate` is the finalized report's end date.
 */
const HouseholdsInTwoGroupsAlert = ({
  householdsInTwoGroups,
}: {
  householdsInTwoGroups: HouseholdInTwoGroups[];
}) => {
  if (householdsInTwoGroups.length === 0) {
    return null;
  }
  const date = new Date().toISOString().substring(0, 10);
  return (
    <Alert variant="danger">
      {householdsInTwoGroups.map((household, index) => {
        const count = household.householdGroups.length;
        const countText = count === 2 ? "two" : `${count}`;
        return (
          <Fragment key={household.householdId}>
            {index === 0 ? null : " "}
            {`Household "${household.householdName}" is a member of ${countText} household groups on ${date}: `}
            {quotedGroupLinks(household.householdGroups)}.
          </Fragment>
        );
      })}
      {" Correct the membership before billing."}
    </Alert>
  );
};

const householdColumnHelper = createColumnHelper<BillingHouseholdRow>();

const BillingSummaryHouseholdTable = ({
  billingSummary,
  households,
  accounts,
  feeStructures,
  viewMode,
  showModal,
  setShowModal,
  handleModalClose,
  setError,
  resetError,
  refetchSummary,
  viewModeSelector,
}: {
  billingSummary?: UnpackResponse<
    BillingController["getBillingSummary"]
  >["data"];
  households?: Household[];
  accounts?: AccountWithMetrics[];
  feeStructures?: FeeStructure[];
  viewMode: string;
  showModal: boolean;
  setShowModal: (val: boolean) => void;
  handleModalClose: () => void;
  setError: (message: string) => void;
  resetError: () => void;
  refetchSummary: () => void;
  viewModeSelector: React.ReactNode;
}) => {
  const columns = useMemo(
    () => [
      householdColumnHelper.display({
        id: "select",
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      }),
      householdColumnHelper.accessor((row) => row.name, {
        id: "household",
        cell: (info) => (
          <Link to={`/clients/households/${info.row.original.id}`}>
            {info.getValue()}
          </Link>
        ),
        header: () => "Household",
      }),
      householdColumnHelper.accessor("noOfAccounts", {
        header: () => "# of Accounts",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      householdColumnHelper.accessor("totalAUM", {
        cell: (info) => formatCurrency(info.getValue()),
        header: () => "Total Assets",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      householdColumnHelper.accessor((row) => row.feeStructureName ?? "", {
        id: "feeStructure",
        cell: (info) => (
          <Link
            to={`/billing/fee-structure/${info.row.original.feeStructureId}`}
          >
            {info.getValue()}
          </Link>
        ),
        header: () => "Fee Structure",
      }),
      householdColumnHelper.accessor((row) => row.householdGroupName ?? "", {
        id: "householdGroup",
        cell: (info) =>
          info.row.original.householdGroupId === null ? null : (
            <Link
              to={`/clients/households/household-groups/${info.row.original.householdGroupId}`}
            >
              {info.getValue()}
            </Link>
          ),
        header: () => "Household Group",
      }),
      householdColumnHelper.accessor("estEffectiveFeeRate", {
        cell: (info) =>
          typeof billingSummary === "undefined" ? (
            <Loading />
          ) : billingSummary === null ? (
            naLabel
          ) : (
            formatPercent(info.getValue(), 2)
          ),
        header: () => "Est. Effective Fee %",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      householdColumnHelper.accessor("estFee", {
        cell: (info) =>
          typeof billingSummary === "undefined" ? (
            <Loading />
          ) : billingSummary === null ? (
            naLabel
          ) : (
            formatCurrency(info.getValue())
          ),
        header: () => "Est. Effective Fee $",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
      householdColumnHelper.accessor("estAnnualFee", {
        cell: (info) =>
          typeof billingSummary === "undefined" ? (
            <Loading />
          ) : billingSummary === null ? (
            naLabel
          ) : (
            formatCurrency(info.getValue())
          ),
        header: () => "Est. Annual Fee $",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
    ],
    [billingSummary],
  );

  const rows = useMemo((): BillingHouseholdRow[] => {
    const feeStructuresMap = _.keyBy(feeStructures, "feeStructureId");

    if (typeof billingSummary === "undefined" || billingSummary === null) {
      const accountsMap = _.groupBy(accounts, "householdId");

      return (households ?? []).map((household) => {
        const householdAccounts = accountsMap[household.id] ?? [];
        const totalAUM = householdAccounts.reduce(
          (result, account) => result + (account.accountBalance ?? 0),
          0,
        );
        // GET /households carries the household's groups on today's date.
        const group = householdGroupOfRow(
          (
            household as Household & {
              householdGroups?: HouseholdGroupForHousehold[];
            }
          ).householdGroups,
        );

        return {
          id: household.id,
          name: household.name,
          noOfAccounts: householdAccounts.length,
          totalAUM,
          estEffectiveFeeRate: 0,
          estAnnualFee: 0,
          estFee: 0,
          ...(group === null
            ? {
                feeStructureId: household.feeStructureId,
                feeStructureName:
                  household.feeStructureId == null
                    ? undefined
                    : feeStructuresMap[household.feeStructureId]?.name,
              }
            : {
                feeStructureId: group.feeStructureId,
                feeStructureName: group.feeStructureName ?? undefined,
              }),
          householdGroupId: group?.householdGroupId ?? null,
          householdGroupName: group?.householdGroupName ?? null,
        };
      });
    } else {
      const accountsMap = _.groupBy(billingSummary.accounts, "householdId");

      return billingSummary.households.map((household) => {
        const householdAccounts = accountsMap[household.id] ?? [];
        // A member household shows its group's fee structure; the household's
        // own `feeStructureId` stays as stored in the response.
        const group = householdGroupOfRow(household.householdGroups);

        return {
          id: household.id,
          name: household.name,
          noOfAccounts: householdAccounts.length,
          totalAUM: household.totalAUM,
          estEffectiveFeeRate: household.estEffectiveFeeRate,
          estFee: household.estFee,
          estAnnualFee: household.estAnnualFee,
          ...(group === null
            ? {
                feeStructureId: household.feeStructureId,
                feeStructureName:
                  household.feeStructureId == null
                    ? ""
                    : (feeStructuresMap[household.feeStructureId]?.name ?? ""),
              }
            : {
                feeStructureId: group.feeStructureId,
                feeStructureName: group.feeStructureName ?? "",
              }),
          householdGroupId: group?.householdGroupId ?? null,
          householdGroupName: group?.householdGroupName ?? null,
        };
      });
    }
  }, [accounts, billingSummary, feeStructures, households]);

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: rows,
    getRowId: (row) => row.id.toString(),
  });

  useEffect(() => {
    setRowSelection({});
  }, [setRowSelection, viewMode]);

  const assignFeeStructureToHouseholds = useAuthenticatedMutationAsync<
    UnpackResponse<HouseholdsController["assignFeeStructureToHouseholds"]>
  >(
    `/v1/households/fee-structure`,
    async (data: { feeStructureId: number; householdIds: number[] }) => ({
      method: "POST",
      body: JSON.stringify(data),
    }),
  );

  const { mutateAsync: onSubmit, isPending: isPendingOnSubmit } = useMutation({
    mutationFn: async (data: {
      selectedId: number | null;
      sourceIds: number[];
    }) => {
      resetError();

      try {
        if (actionMode === 1) {
          await assignFeeStructureToHouseholds({
            feeStructureId: data.selectedId,
            householdsIds: data.sourceIds,
          });
        }

        setShowModal(false);
        refetchSummary();
      } catch (err) {
        // The api refuses a household that is a member of a household group
        // with a 409 whose message names each such household and its group.
        const conflictMessage =
          err instanceof ApiError && err.status === 409
            ? serverError(err)?.body?.message
            : undefined;
        const message =
          typeof conflictMessage === "string"
            ? conflictMessage
            : "Failed to assign to households";
        setError(message);
        console.error(message, err);
      }
    },
  });

  const handleSubmit = useCallback(
    (toAssignId: number | null) => {
      const sourceIds = Object.keys(
        table.getFilteredSelectedRowModel().rowsById,
      ).map((idStr) => parseInt(idStr));
      onSubmit({ selectedId: toAssignId, sourceIds });
    },
    [table, onSubmit],
  );

  const [actionMode, setActionMode] = useState(0);
  const handleActionItemClick = (action: number) => {
    setActionMode(action);
    setShowModal(true);
  };

  return (
    <>
      <Col>
        <Row>
          {viewModeSelector}
          <Col className="mb-3 d-flex justify-content-end">
            <DropdownButton title="Actions">
              <Dropdown.Item as="button" disabled>
                Export to Excel
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                onClick={() => handleActionItemClick(1)}
                disabled={Object.keys(rowSelection).length === 0}
              >
                Assign Fee Structures
              </Dropdown.Item>
            </DropdownButton>
          </Col>
        </Row>
      </Col>
      <Table table={table} />
      <ItemAssigningModal
        showModal={showModal}
        handleModalClose={handleModalClose}
        data={(feeStructures ?? []).map((feeStructure) => ({
          id: feeStructure.feeStructureId,
          name: feeStructure.name,
        }))}
        header="Assign Fee Structure to Households"
        description={`${Object.keys(rowSelection).length} Households Selected`}
        targetTitle="Fee Structure"
        isSubmitting={isPendingOnSubmit}
        handleSubmit={handleSubmit}
        allowRemoval
      />
    </>
  );
};

type BillingAccountRow = {
  id: number;
  name: string;
  householdId: number | null;
  householdName?: string;
  aum: number;
  cash: number;
  feeStructureId: number | null | undefined;
  feeStructureName?: string;
  isFeeStructureInherited: boolean;
  feeLocation?: "S" | "G" | "A" | null;
  feeLocationAccountId?: number;
  feeLocationAccountName?: string;
  cashOrNextFee?: number;
};

const accountColumnHelper = createColumnHelper<BillingAccountRow>();

const BillingSummaryAccountTable = ({
  billingSummary,
  households,
  accounts,
  feeStructures,
  viewMode,
  showModal,
  setShowModal,
  handleModalClose,
  setError,
  resetError,
  refetchSummary,
  viewModeSelector,
}: {
  billingSummary?: UnpackResponse<
    BillingController["getBillingSummary"]
  >["data"];
  households?: Household[];
  accounts?: AccountWithMetrics[];
  feeStructures?: FeeStructure[];
  viewMode: string;
  showModal: boolean;
  setShowModal: (val: boolean) => void;
  handleModalClose: () => void;
  setError: (message: string) => void;
  resetError: () => void;
  refetchSummary: () => void;
  viewModeSelector: React.ReactNode;
}) => {
  const columns = useMemo(
    () => [
      accountColumnHelper.display({
        id: "select",
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      }),
      accountColumnHelper.accessor((row) => row.name, {
        id: "account",
        cell: (info) => (
          <Link to={`/clients/accounts/${info.row.original.id}`}>
            {info.getValue()}
          </Link>
        ),
        header: () => "Account",
      }),
      accountColumnHelper.accessor((row) => row.householdName ?? "", {
        id: "household",
        cell: (info) => (
          <Link to={`/clients/households/${info.row.original.householdId}`}>
            {info.getValue()}
          </Link>
        ),
        header: () => "Household",
      }),
      accountColumnHelper.accessor((row) => row.aum, {
        id: "aum",
        cell: (info) => formatCurrency(+info.getValue()),
        header: () => "AUM",
        enableColumnFilter: false,
      }),
      accountColumnHelper.accessor((row) => row.cash, {
        id: "cash",
        cell: (info) => formatPercent(info.getValue(), 2),
        header: () => "Cash %",
        enableColumnFilter: false,
      }),
      accountColumnHelper.accessor((row) => row.feeStructureName ?? "", {
        id: "feeStructure",
        cell: (info) => (
          <Link
            to={`/billing/fee-structure/${info.row.original.feeStructureId}`}
          >
            {info.row.original.isFeeStructureInherited ? (
              <em>{info.getValue()}</em>
            ) : (
              info.getValue()
            )}
          </Link>
        ),
        header: () => "Fee Structure",
      }),
      accountColumnHelper.accessor(
        (row) =>
          feeLocationDisplay(
            row.feeLocation ?? "S",
            row.feeLocationAccountName,
          ),
        {
          id: "feeLocation",
          cell: (info) =>
            info.row.original.feeLocation !== "A" ? (
              info.getValue()
            ) : (
              <Link
                to={`/clients/accounts/${info.row.original.feeLocationAccountId}`}
              >
                {info.getValue()}
              </Link>
            ),
          header: () => "Fee Location",
          enableColumnFilter: false,
        },
      ),
    ],
    [],
  );

  const rows = useMemo((): BillingAccountRow[] => {
    // The summary response carries the firm's accounts and households, so read
    // them from it and leave the separately fetched sets to the fallback path,
    // where the firm has finalized no report and there is no summary to read.
    // The row source already worked this way; the two maps below did not, and
    // so were keyed off a different set of accounts than the rows they served.
    const sourceAccounts = billingSummary?.accounts ?? accounts ?? [];
    // Annotated, because the summary's households carry the report's figures on
    // top of the fetched ones and `keyBy` will not read a union of two array
    // types as a collection. Only `name` and `feeStructureId` are read below,
    // and both sides of the union are households.
    const sourceHouseholds: Household[] =
      billingSummary?.households ?? households ?? [];

    const householdsMap = _.keyBy(sourceHouseholds, "id");
    const accountsMap = _.keyBy(sourceAccounts, "id");
    const feeStructuresMap = _.keyBy(feeStructures, "feeStructureId");

    /*
      Which of the firm's accounts the `account_active` view holds
      (`db/sql/V150__add_cash_split_columns.sql:55`), which is the question the
      fee-location column below asks and NOT the one `AccountWithMetrics.isActive`
      answers — that field is `archived_time > now` alone
      (`api/src/accounts/accounts.service.ts:240`), and the view narrows it
      further by the advisor code's close date and the account's status.

      The summary states the membership outright. Without one, the page is on
      the fallback path and `accounts` came from GET /accounts, which selects
      `FROM account_active` whenever `includeInactive` is false
      (`api/src/accounts/accounts.service.ts:490`) — so on that path the fetched
      set IS the membership, which is how the check below was posed before the
      summary carried it.
    */
    const activeAccountIds = new Set(
      billingSummary?.activeAccountIds ??
        (accounts ?? []).map((account) => account.id),
    );

    const result: BillingAccountRow[] = sourceAccounts.map(
      (
        account: AccountWithMetrics &
          Partial<{
            billing: BillingReportAccount & { billableAUM: number };
          }>,
      ) => {
        const accountHousehold =
          account.householdId == null
            ? undefined
            : householdsMap[account.householdId];
        const feeStructureId =
          account.feeStructureId ?? accountHousehold?.feeStructureId;

        // Catch cases where a relocated account is no longer active. An
        // advisor acts on this label (an-decision-01a0737dc64d54ff): the
        // account this one's fees are debited from is one the firm can no
        // longer bill, and the column reads "Relocated" in place of its name
        // (`lib.tsx:70`) so the pair can be found and repointed.
        //
        // `accountsMap` also has to hold the account, because its name is read
        // from that map below. It is a superset of the membership on both paths
        // — every `account_active` row is among the firm's accounts — so this
        // second arm never decides the label on its own; it is here so a lookup
        // that somehow misses degrades to the warning rather than throwing.
        const feeLocationAccount =
          account.feeLocationAccountId == null ||
          !activeAccountIds.has(account.feeLocationAccountId)
            ? undefined
            : accountsMap[account.feeLocationAccountId];
        const feeLocationAccountId = feeLocationAccount?.id;

        return {
          id: account.id,
          name: displayAccountName(account.displayName, account.displayNumber),
          householdId: account.householdId,
          householdName: accountHousehold?.name,
          aum: account.accountBalance ?? 0,
          cash:
            (account.accountBalance ?? 0) === 0
              ? 0
              : (account.cashBalance ?? 0) / (account.accountBalance ?? 0),
          feeStructureId: feeStructureId,
          feeStructureName:
            feeStructureId == null
              ? undefined
              : feeStructuresMap[feeStructureId]?.name,
          isFeeStructureInherited:
            feeStructureId != null && account.feeStructureId == null,
          cashOrNextFee:
            typeof billingSummary === "undefined"
              ? undefined
              : typeof account.billing?.totalDebited === "undefined" ||
                  account.billing.totalDebited === 0
                ? undefined
                : (account.cashBalance ?? 0) / account.billing.totalDebited,
          feeLocation: account.feeLocation,
          feeLocationAccountId,
          feeLocationAccountName:
            account.feeLocation !== "A" ||
            typeof feeLocationAccount === "undefined"
              ? undefined
              : displayAccountName(
                  feeLocationAccount.displayName,
                  feeLocationAccount.displayNumber,
                ),
        };
      },
    );

    const accountsPayingForOthers = result
      .filter((account) => account.feeLocation === "A")
      .map((account) => account.feeLocationAccountId as number);

    return result.filter(
      (account) =>
        viewMode === "Account" ||
        (account.householdId == null &&
          viewMode === "Accounts without households") ||
        (account.feeStructureId == null &&
          viewMode === "Accounts without fee structure") ||
        (viewMode === "Accounts paying for other accounts" &&
          accountsPayingForOthers.includes(account.id)) ||
        (viewMode === "Accounts with relocated fees" &&
          account.feeLocation === "A"),
    );
  }, [households, feeStructures, billingSummary, accounts, viewMode]);

  const { table, setRowSelection } = useTable({
    columns,
    data: rows,
    getRowId: (row) => row.id.toString(),
  });

  useEffect(() => {
    setRowSelection({});
  }, [setRowSelection, viewMode]);

  const assignHouseholdToAccounts = useAuthenticatedMutationAsync<
    UnpackResponse<HouseholdsController["mapAccountsToHouseholds"]>
  >(`/households/accounts/assign`, async (data: Record<number, number[]>) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const assignFeeStructureToAccounts = useAuthenticatedMutationAsync<
    UnpackResponse<AccountsController["assignFeeStructureToAccounts"]>
  >(
    // `POST /accounts/fee-structure` is served at `/v1` only (APP-1653).
    `/v1/accounts/fee-structure`,
    async (data: { feeStructureId: number; accountsIds: number[] }) => ({
      method: "POST",
      body: JSON.stringify(data),
    }),
  );

  const { mutateAsync: onSubmit, isPending: isPendingOnSubmit } = useMutation({
    mutationFn: async (data: {
      selectedId: number | null;
      sourceIds: number[];
    }) => {
      resetError();

      try {
        if (actionMode === 1) {
          await assignHouseholdToAccounts(
            data.selectedId === null
              ? { null: data.sourceIds }
              : {
                  [data.selectedId]: data.sourceIds,
                },
          );
        } else if (actionMode === 2) {
          await assignFeeStructureToAccounts({
            feeStructureId: data.selectedId,
            accountsIds: data.sourceIds,
          });
        }

        setShowModal(false);
        refetchSummary();
      } catch (err) {
        const message = "Failed to assign to accounts";
        setError(message);
        console.error(message, err);
      }
    },
  });

  const handleSubmit = useCallback(
    (toAssignId: number | null) => {
      const sourceIds = Object.keys(
        table.getFilteredSelectedRowModel().rowsById,
      ).map((idStr) => parseInt(idStr));
      onSubmit({ selectedId: toAssignId, sourceIds });
    },
    [onSubmit, table],
  );

  const [actionMode, setActionMode] = useState(0);
  const handleActionItemClick = (action: number) => {
    setActionMode(action);
    setShowModal(true);
  };

  return (
    <>
      <Col>
        <Row>
          {viewModeSelector}
          <Col className="mb-3 d-flex justify-content-end">
            <DropdownButton title="Actions">
              <Dropdown.Item as="button" disabled>
                Export to Excel
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                onClick={() => handleActionItemClick(1)}
                disabled={
                  Object.keys(table.getFilteredSelectedRowModel().rowsById)
                    .length === 0
                }
              >
                Assign Accounts to Households
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                onClick={() => handleActionItemClick(2)}
                disabled={
                  Object.keys(table.getFilteredSelectedRowModel().rowsById)
                    .length === 0
                }
              >
                Assign Fee Structures to Accounts
              </Dropdown.Item>
            </DropdownButton>
          </Col>
        </Row>
      </Col>
      <Table table={table} />
      <ItemAssigningModal
        showModal={showModal}
        handleModalClose={handleModalClose}
        data={
          actionMode === 1
            ? (billingSummary?.households ?? [])
            : (feeStructures ?? []).map((feeStructure) => ({
                id: feeStructure.feeStructureId,
                name: feeStructure.name,
              }))
        }
        header={
          actionMode === 1
            ? "Assign Accounts to Household"
            : "Assign Fee Structure to Accounts"
        }
        description={`${
          Object.keys(table.getFilteredSelectedRowModel().rowsById).length
        } Accounts Selected`}
        targetTitle={actionMode === 1 ? "Household" : "Fee Structure"}
        isSubmitting={isPendingOnSubmit}
        handleSubmit={handleSubmit}
        allowRemoval
      />
    </>
  );
};

const ViewModeSelector = ({
  viewMode,
  setViewMode,
}: {
  viewMode: string;
  setViewMode: (val: string) => void;
}) => {
  const handleChangeViewMode = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setViewMode(e.target.value);
    },
    [setViewMode],
  );

  return (
    <Col sm={8} md={6} xl={4} xxl={3} className="mb-3">
      <Form.Group controlId="form-calculation-type">
        <Form.Label>View by</Form.Label>
        <Form.Select value={viewMode} onChange={handleChangeViewMode}>
          {[
            "Household",
            "Account",
            "Accounts without households",
            "Accounts without fee structure",
            "Accounts paying for other accounts",
            "Accounts with relocated fees",
          ].map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </Col>
  );
};

const BillingSummary = () => {
  const {
    isPending: isPendingSummary,
    isError: isErrorSummary,
    data: summaryBody,
    refetch: refetchSummary,
  } = useAuthenticatedFetch<
    SerializedObject<UnpackResponse<BillingController["getBillingSummary"]>>
  >(`/billing/billing-summary`);

  const billingSummary = useMemo(
    () =>
      typeof summaryBody?.data === "undefined"
        ? undefined
        : summaryBody.data === null
          ? null
          : {
              ...summaryBody.data,
              asOfDate: parseISODateNaive(summaryBody.data.asOfDate),
              accounts: summaryBody.data.accounts.map((account) => ({
                ...deserializeAccountWithMetrics(account),
                billing:
                  typeof account.billing === "undefined"
                    ? undefined
                    : {
                        ...account.billing,
                        startDate: parseISODateNaive(account.billing.startDate),
                        endDate: parseISODateNaive(account.billing.endDate),
                      },
              })),
              households: summaryBody.data.households.map(
                deserializeHouseholdWithMetrics,
              ),
            },
    [summaryBody?.data],
  );

  /*
    `billingSummary` has three states: `undefined` while the request is in
    flight or has failed, `null` when the firm has finalized no report, and the
    overview otherwise. Only the third carries accounts and households, and
    those are the same figures `/accounts` would recompute, so ask for them
    separately only in the two states that have no summary to read them from.
    Both tables keep a fallback that builds rows from these requests, and the
    household table's runs whenever the summary is absent, so withdrawing them
    in either of those states would leave the page permanently empty for a firm
    that has finalized nothing.
  */
  const hasBillingSummary =
    typeof billingSummary !== "undefined" && billingSummary !== null;
  const needsAccountsAndHouseholds = !isPendingSummary && !hasBillingSummary;

  const {
    isPending: isPendingHouseholds,
    isError: isErrorHouseholds,
    data: households,
  } = useQueryHouseholds({ enabled: needsAccountsAndHouseholds });

  const {
    isPending: isPendingAccounts,
    isError: isErrorAccounts,
    data: accountsBody,
  } = useQueryAllAccounts({
    includeBalances: true,
    enabled: needsAccountsAndHouseholds,
  });
  const accounts = accountsBody?.data ?? [];

  const { isPending: isPendingFeeStructures, data: feeStructuresBody } =
    useQueryFeeStructures();
  const feeStructures = feeStructuresBody?.data;

  const { setError, resetError, errorMessage } = useErrorMessage();

  const [viewMode, setViewMode] = useState("Household");
  const [showModal, setShowModal] = useState(false);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  // With no finalized report the summary has no `householdsInTwoGroups`, so
  // the households in two or more groups are read off GET /households.
  const householdsInTwoGroupsWithoutSummary = useMemo(
    (): HouseholdInTwoGroups[] =>
      (households?.data ?? []).flatMap((household) => {
        const householdGroups =
          (
            household as Household & {
              householdGroups?: HouseholdGroupForHousehold[];
            }
          ).householdGroups ?? [];
        return householdGroups.length < 2
          ? []
          : [
              {
                householdId: household.id,
                householdName: household.name,
                householdGroups: householdGroups.map(
                  ({ householdGroupId, householdGroupName }) => ({
                    householdGroupId,
                    householdGroupName,
                  }),
                ),
              },
            ];
      }),
    [households?.data],
  );

  const viewModeSelector = (
    <ViewModeSelector viewMode={viewMode} setViewMode={setViewMode} />
  );

  return (
    <TabContainerWithTabs tabs={BillingNav}>
      {
        <>
          {errorMessage}
          <Row>
            <Col xl={12}>
              <ButtonToolbar className="mb-3">
                <ButtonGroup className="me-3">
                  <ActionButton
                    variant="secondary"
                    label="Run Firm-Wide Billing"
                    icon="/icons/new.svg"
                    className="btn-action_secondary_wide"
                    as={Link}
                    to="/billing/reports/run?type=standard"
                  />
                </ButtonGroup>
                <ButtonGroup>
                  <ActionButton
                    variant="secondary"
                    label="Run One-Off Billing"
                    icon="/icons/new.svg"
                    className="btn-action_secondary_wide"
                    as={Link}
                    to="/billing/reports/run?type=custom"
                  />
                </ButtonGroup>
              </ButtonToolbar>
            </Col>
          </Row>
          <Row>
            <Col>
              <h3>Billing Summary</h3>
              {isPendingSummary ? (
                <Loading />
              ) : isErrorSummary ? (
                <Alert variant="danger">An error occurred</Alert>
              ) : billingSummary === null ? (
                /*
                  The overview reports the last finalized billing report, and
                  the firm has finalized none — which is every firm until it
                  finalizes for the first time, because the finalize schema
                  leaves existing reports at their current status
                  (an-decision-019fb9e2e612916e). Nothing has gone wrong and the
                  firm may well have run many reports, so this is a default
                  Alert saying which act is missing and where to perform it —
                  not a danger Alert, and not "no billing data"
                  (s-billing-report-finalization-capability-019fba175c3d304d
                  R31 / AC28).
                */
                <>
                  <Alert>
                    <p className="mb-0">
                      No billing report has been finalized yet.
                    </p>
                    <p className="mb-0">
                      Finalize a billing report on the{" "}
                      <Link to="/billing/reports">Billing Reports</Link> tab and
                      its figures appear here.
                    </p>
                  </Alert>
                  <HouseholdsInTwoGroupsAlert
                    householdsInTwoGroups={householdsInTwoGroupsWithoutSummary}
                  />
                </>
              ) : (
                <>
                  <small>
                    *Based on the last finalized billing report from{" "}
                    {billingSummary?.asOfDate.toLocaleDateString()}
                  </small>
                  <HouseholdsInTwoGroupsAlert
                    householdsInTwoGroups={
                      billingSummary?.householdsInTwoGroups ?? []
                    }
                  />
                  <Row>
                    <Col xxl={3} md={6}>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={6}>
                                AUM with Fees
                              </Col>
                              <Col as="dd">
                                {formatCurrency(
                                  billingSummary?.aumWithFees ?? 0,
                                )}
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={6}>
                                AUM with 0% Fee
                              </Col>
                              <Col as="dd">
                                {formatCurrency(
                                  billingSummary?.unbilledAUM ?? 0,
                                )}
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col xxl={3} md={6}>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={6}>
                                <span className="align-middle me-1">
                                  Effective Fee %
                                </span>
                                <InfoTooltip tooltip="The effective fee in parentheses includes any accounts with 0% fee" />
                              </Col>
                              <Col as="dd">
                                {formatPercent(
                                  billingSummary?.effectiveFeeRateBillableAUM ??
                                    0,
                                  2,
                                )}{" "}
                                (
                                {formatPercent(
                                  billingSummary?.effectiveFeeRateTotalAUM ?? 0,
                                  2,
                                )}
                                )
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={6}>
                                Est. Annual Revenue
                              </Col>
                              <Col as="dd">
                                {formatCurrency(
                                  billingSummary?.annualRevenue ?? 0,
                                )}
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col xxl={3} md={6}>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={6}>
                                Households
                              </Col>
                              <Col as="dd">
                                <Link to={`/clients/households`}>
                                  {billingSummary?.households.length.toLocaleString()}
                                </Link>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={6}>
                                Accounts
                              </Col>
                              <Col as="dd">
                                <Link to={`/clients/accounts`}>
                                  {billingSummary?.accounts.length.toLocaleString()}
                                </Link>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col xxl={3} md={6}>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={5}>
                                Accounts w/o Fee Structure
                              </Col>
                              <Col as="dd">
                                <Link
                                  to={`/clients/accounts?${billingSummary?.accountsWithoutFeeStructures
                                    .map((id) => `id=${id}`)
                                    .join("&")}`}
                                >
                                  {billingSummary?.accountsWithoutFeeStructures.length.toLocaleString()}
                                </Link>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col>
                          <div className="text-center item-card">
                            <Row className="h-100 align-items-center">
                              <Col as="dt" sm={5}>
                                Accounts w/o Household
                              </Col>
                              <Col as="dd">
                                <Link
                                  to={`/clients/accounts?${billingSummary?.accountsWithoutHouseholds
                                    .map((id) => `id=${id}`)
                                    .join("&")}`}
                                >
                                  {billingSummary?.accountsWithoutHouseholds.length.toLocaleString()}
                                </Link>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
          <Row className="mt-3">
            {/*
              A disabled query reports `isPending` forever, because it has no
              data and never will, so the two account and household requests
              can only gate the table in the states that actually issue them.
              The summary takes their place in the state that does not, and it
              has to arrive before `needsAccountsAndHouseholds` can be read.
            */}
            {isPendingSummary ||
            isPendingFeeStructures ||
            (needsAccountsAndHouseholds &&
              (isPendingHouseholds || isPendingAccounts)) ? (
              <Loading />
            ) : isErrorHouseholds || isErrorAccounts ? (
              <Alert variant="danger">Failed to load households/accounts</Alert>
            ) : viewMode.includes("Account") ? (
              <BillingSummaryAccountTable
                billingSummary={billingSummary}
                households={households?.data}
                accounts={accounts}
                feeStructures={feeStructures}
                viewMode={viewMode}
                showModal={showModal}
                setShowModal={setShowModal}
                handleModalClose={handleModalClose}
                setError={setError}
                resetError={resetError}
                refetchSummary={refetchSummary}
                viewModeSelector={viewModeSelector}
              />
            ) : (
              <BillingSummaryHouseholdTable
                billingSummary={billingSummary}
                households={households?.data}
                accounts={accounts}
                feeStructures={feeStructures}
                viewMode={viewMode}
                showModal={showModal}
                setShowModal={setShowModal}
                handleModalClose={handleModalClose}
                setError={setError}
                resetError={resetError}
                refetchSummary={refetchSummary}
                viewModeSelector={viewModeSelector}
              />
            )}
          </Row>
        </>
      }
    </TabContainerWithTabs>
  );
};

export default BillingSummary;
